import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { toast } from "sonner";
import ClearTestDataButton from "@/components/admin/ClearTestDataButton";
import ConfirmDeleteDialog from "@/components/admin/ConfirmDeleteDialog";

const statusColors = {
  open: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-800",
  resolved: "bg-green-100 text-green-800",
};

export default function SupportPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [deleting, setDeleting] = useState(null);   // ticket pending delete
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data));
  useEffect(() => { load(); }, []);

  const openTicket = async (t) => {
    setOpen(t);
    const r = await api.get(`/support/tickets/${t.id}`);
    setDetail(r.data); setReply(""); setNewStatus(r.data.ticket.status);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/support/tickets/${open.id}/reply`, { message: reply, status: newStatus !== open.status ? newStatus : undefined });
      toast.success("Reply sent");
      const r = await api.get(`/support/tickets/${open.id}`); setDetail(r.data); setReply("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/support/tickets/${deleting.id}`);
      toast.success("Ticket deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setDeleteBusy(false);
    }
  };

  const filtered = tickets.filter((t) => {
    if (filter !== "all" && t.status !== filter) return false;
    if (search && !`${t.subject} ${t.student_name} ${t.category}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="support-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Support</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Tickets</h1>
        </div>
        {user?.role === "super_admin" && tickets.length > 0 && (
          <ClearTestDataButton
            role={user.role}
            title="Clear All Tickets"
            description={`Permanently delete all ${tickets.length} support ticket(s) and every reply.`}
            endpoint="/admin/support/tickets/clear-all"
            confirmPhrase="DELETE TICKETS"
            onCleared={() => load()}
            dataTestId="clear-tickets"
          />
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-3">
        {filtered.length === 0 ? <Card className="p-8 text-center text-slate-500">No tickets</Card> :
          filtered.map((t) => (
            <Card key={t.id} className="p-5 border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openTicket(t)} data-testid={`ticket-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge className={`${statusColors[t.status]} hover:opacity-90 capitalize`}>{t.status}</Badge>
                    <Badge variant="outline">{t.category}</Badge>
                    <span className="text-xs text-slate-500">{t.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="font-semibold">{t.subject}</div>
                  <div className="text-sm text-slate-600 line-clamp-1 mt-1">{t.message}</div>
                  <div className="text-xs text-slate-500 mt-2">From: {t.student_name} ({t.student_email})</div>
                </div>
                {user?.role === "super_admin" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 shrink-0"
                    onClick={(e) => { e.stopPropagation(); setDeleting(t); }}
                    title="Delete ticket"
                    data-testid={`delete-ticket-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => { if (!v) { setOpen(null); setDetail(null); setReply(""); } }}>
        <DialogContent className="max-w-2xl p-0 gap-0 flex flex-col h-[80vh] max-h-[720px]" data-testid="admin-ticket-detail-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="pr-8 leading-tight">{open?.subject}</DialogTitle>
            {open && (
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <span className="text-[11px] text-slate-500">
                  {open.student_name} · {(open.created_at || "").slice(0, 16).replace("T", " ")}
                </span>
              </div>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50 dark:bg-slate-950/40" data-testid="admin-ticket-chat-scroll">
            {detail && (
              <>
                {/* Original student message = left */}
                <AdminChatBubble
                  side="left"
                  name={detail.ticket.student_name || "Student"}
                  when={detail.ticket.created_at}
                  message={detail.ticket.message}
                  testId="admin-ticket-msg-original"
                />
                {detail.replies.map((r) => (
                  <AdminChatBubble
                    key={r.id}
                    side={r.sender_role === "student" ? "left" : "right"}
                    name={r.sender_name || (r.sender_role === "student" ? "Student" : "Support")}
                    when={r.created_at}
                    message={r.message}
                    testId={`admin-ticket-reply-${r.id}`}
                  />
                ))}
              </>
            )}
          </div>
          {detail && hasPerm(user, "support", "reply") && (
            <div className="border-t border-slate-100 dark:border-slate-800 p-3 bg-white dark:bg-slate-900 shrink-0">
              <Textarea
                rows={2}
                placeholder="Type your reply…  (Enter = send, Shift+Enter = new line)"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
                }}
                className="resize-none"
                data-testid="reply-text"
              />
              <div className="flex justify-between items-center gap-2 mt-2">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="w-36" data-testid="reply-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
                <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={sendReply} data-testid="send-reply-btn" disabled={!reply.trim()}>Send Reply</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleting}
        onCancel={() => { if (!deleteBusy) setDeleting(null); }}
        onConfirm={confirmDelete}
        title="Delete this ticket?"
        itemLabel={deleting?.subject}
        description="All replies on this ticket will also be permanently deleted."
        busy={deleteBusy}
        confirmLabel="Delete ticket"
        dataTestId="delete-ticket-confirm"
      />
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
}

function AdminChatBubble({ side, name, when, message, testId }) {
  const isRight = side === "right";
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className={`flex items-end gap-2 ${isRight ? "flex-row-reverse" : "flex-row"}`} data-testid={testId}>
      <div className={`h-8 w-8 rounded-full grid place-items-center text-xs font-bold shrink-0 text-white ${
        isRight ? "bg-gradient-to-br from-indigo-500 to-violet-600" : "bg-gradient-to-br from-slate-500 to-slate-700"
      }`}>{initial}</div>
      <div className={`max-w-[78%] ${isRight ? "items-end" : "items-start"} flex flex-col`}>
        <div className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-snug shadow-sm ${
          isRight
            ? "bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-br-sm"
            : "bg-white dark:bg-slate-800 dark:text-slate-100 text-slate-800 border border-slate-200 dark:border-slate-700 rounded-bl-sm"
        }`}>{message}</div>
        <div className={`text-[10px] text-slate-500 mt-1 px-1 ${isRight ? "text-right" : "text-left"}`}>
          {name} · {formatWhen(when)}
        </div>
      </div>
    </div>
  );
}
